import { defineNamespace } from '../registry';
const ja = {
  originalAccount: '契約の接続アカウント',
  retry: '状態を再取得',
  agreementIntro:
    '案件を作成する前でも電子契約を利用できます。署名依頼メールは、契約ごとに選択した電子署名サービスから届きます。',
  historyHelp:
    '電子署名サービスを接続すると新しい契約を作成・送信できます。接続を解除しても、これまでの契約と署名済み文書は残ります。',
  title: 'DocuSign 電子署名連携',
  description: 'DocuSign と連携し、契約テンプレートから署名依頼を管理します。',
  environment: '利用環境',
  demo: 'デモ',
  production: '本番',
  unconfigured: '未設定',
  setupHelp:
    'Integration Key、Client Secret、Connect の HMAC キーを登録してから、DocuSign で接続を許可してください。送信前に宛先を固定できる DocuSign アカウントが必要です。再設定すると認可とアカウント選択をやり直します。',
  secret: 'Client Secret',
  hmac: 'Connect HMAC キー',
  hmacHelp: '最大2本。更新時は有効なキーをすべて入力します。保存した秘密値は再表示しません。',
  save: '設定を保存',
  saving: '処理中...',
  authorize: 'DocuSign で接続を許可',
  saved: '設定を保存しました。DocuSign で接続を許可してください。',
  failed: '処理できませんでした。設定を確認してやり直してください。',
  invalidUrl: '安全な認可先を確認できませんでした。接続をやり直してください。',
  account: '利用する DocuSign アカウント',
  selectAccount: 'アカウントを選択',
  confirmAccount: 'このアカウントで接続',
  accountHelp: 'DocuSign が返した候補から、事務所で利用するアカウントを選んでください。',
  connected: 'DocuSign に接続しました。',
  disconnect: '接続を解除',
  disconnectTitle: 'DocuSign 連携の解除',
  disconnectMessage:
    '設定と接続を解除します。契約履歴と署名済み文書は残ります。署名待ち契約の完了確認には、元のアカウントで再接続する必要があります。',
  resetTitle: 'DocuSign の設定を更新',
  resetMessage:
    '設定を更新すると認可とアカウント選択が解除され、登録した HMAC キーを置き換えます。契約履歴と保存済み PDF は残ります。署名待ち契約の完了確認には、元のアカウントで再接続する必要があります。',
  disconnected: 'DocuSign の接続を解除しました。',
  noPermission: 'この操作には外部連携の編集権限が必要です。',
  loading: '読み込み中...',
  webhook: 'Connect の通知先 URL',
  webhookReady: 'HMAC 設定済み',
  webhookMissing: 'Connect HMAC キーが必要です。',
  callbackTitle: 'DocuSign の接続',
  callbackWait: 'DocuSign の認可を確認しています。',
  restart: '認可を完了できませんでした。組織設定から接続をやり直してください。',
  loginAgain:
    'ログイン期限が切れています。ログインした後、組織設定から DocuSign の接続をやり直してください。',
  login: 'ログインへ',
  settings: '組織設定へ戻る',
  provider: '電子署名サービス',
  template: 'DocuSign テンプレート',
  chooseTemplate: 'テンプレートを選択',
  role: '署名者の役割',
  chooseRole: '役割を選択',
  oneRole:
    '署名者の役割が1つだけのテンプレートを選んでください。固定の宛先、CC、複数の署名者は利用できません。',
  roleRequired: 'テンプレートと署名者の役割を選択してください。',
  templateFailed: 'テンプレートを取得できませんでした。',
  sendLockUnavailable:
    '送信していません。DocuSign でこの下書きを編集中なら閉じてから再試行してください。解消しない場合は、管理者に送信前の宛先固定を利用できるアカウントか確認してください。',
  sendGuardUnavailable:
    '送信前の宛先確認を完了できなかったため、送信していません。時間を空けて再試行してください。',
  sendRecipientMismatch:
    'DocuSign の下書きの宛先がこの確認画面と異なるため、送信していません。下書きの宛名・メールアドレス・役割を確認し、追加の宛先やメール以外の通知設定を外してください。宛先は自動変更しません。',
  checkResult: '送信結果を確認',
  checkStatus: '署名状況を確認',
  checkWait: '確認中...',
  checkFailed:
    '結果を確認できませんでした。間隔を空けて再確認してください。再送信は行っていません。',
  providerFixed: '作成後に電子署名サービス・環境・アカウントは変更できません。',
  sendWarning:
    '確認すると、選択した電子署名サービスから宛先へ署名依頼メールが送られます。送信後は取り消せない操作です。',
} as const;
const en = {
  originalAccount: 'Agreement account',
  retry: 'Reload status',
  agreementIntro:
    'Use electronic agreements before creating a case. Signature request emails are sent by the service selected for each agreement.',
  historyHelp:
    'Connect an e-signature service to create and send new agreements. Existing agreements and signed documents are retained after disconnecting.',
  title: 'DocuSign e-signature integration',
  description: 'Manage signature requests using DocuSign agreement templates.',
  environment: 'Environment',
  demo: 'Demo',
  production: 'Production',
  unconfigured: 'Not configured',
  setupHelp:
    'Enter the Integration Key, Client Secret and Connect HMAC keys, then authorize the connection in DocuSign. The account must support locking recipients before sending. Changing the configuration resets authorization and account selection.',
  secret: 'Client Secret',
  hmac: 'Connect HMAC keys',
  hmacHelp:
    'Up to two keys. Enter all active keys when updating. Saved secrets are never displayed.',
  save: 'Save configuration',
  saving: 'Processing...',
  authorize: 'Authorize in DocuSign',
  saved: 'Configuration saved. Authorize the connection in DocuSign.',
  failed: 'Unable to complete the operation. Check the configuration and try again.',
  invalidUrl: 'Unable to verify a safe authorization destination. Start the connection again.',
  account: 'DocuSign account to use',
  selectAccount: 'Select an account',
  confirmAccount: 'Connect this account',
  accountHelp: 'Select your office account from the accounts returned by DocuSign.',
  connected: 'Connected to DocuSign.',
  disconnect: 'Disconnect',
  disconnectTitle: 'Disconnect DocuSign',
  disconnectMessage:
    'Remove the configuration and connection. Agreement history and signed documents will be retained. Reconnect the original account to check completion of agreements awaiting signature.',
  resetTitle: 'Update DocuSign configuration',
  resetMessage:
    'Updating resets authorization and account selection and replaces the registered HMAC keys. Agreement history and saved PDFs remain. Reconnect the original account to check completion of agreements awaiting signature.',
  disconnected: 'DocuSign disconnected.',
  noPermission: 'Integration edit permission is required.',
  loading: 'Loading...',
  webhook: 'Connect notification URL',
  webhookReady: 'HMAC configured',
  webhookMissing: 'Connect HMAC keys are required.',
  callbackTitle: 'Connect DocuSign',
  callbackWait: 'Checking DocuSign authorization.',
  restart:
    'Unable to complete authorization. Start the connection again from organization settings.',
  loginAgain:
    'Your session has expired. Sign in, then start the DocuSign connection again from organization settings.',
  login: 'Sign in',
  settings: 'Return to organization settings',
  provider: 'E-signature service',
  template: 'DocuSign template',
  chooseTemplate: 'Select a template',
  role: 'Signer role',
  chooseRole: 'Select a role',
  oneRole:
    'Choose a template with exactly one signer role. Fixed recipients, CC recipients and multiple signers are not supported.',
  roleRequired: 'Select a template and signer role.',
  templateFailed: 'Unable to load templates.',
  sendLockUnavailable:
    'Nothing was sent. Close this draft if it is being edited in DocuSign, then try again. If the issue continues, ask an administrator whether the account supports locking recipients before sending.',
  sendGuardUnavailable:
    'Nothing was sent because the recipient check could not be completed. Wait before trying again.',
  sendRecipientMismatch:
    'Nothing was sent because the DocuSign draft recipients differ from this confirmation. Check the name, email and signer role, and remove additional recipients or non-email notifications. Recipients are not changed automatically.',
  checkResult: 'Check send result',
  checkStatus: 'Check signature status',
  checkWait: 'Checking...',
  checkFailed: 'Unable to verify the result. Wait before checking again. No request was resent.',
  providerFixed:
    'The e-signature service, environment and account cannot be changed after creation.',
  sendWarning:
    'Confirmation sends a signature request email to the recipient using the selected e-signature service. This action cannot be undone after sending.',
} as const;
export const docusignNs = defineNamespace('docusign', ja, en);
